import { useMutation, useQuery, useQueryClient } from '@/hooks/useApi';
import * as api from '@/services/api';
import type {
  AiProcessingStats,
  AiProcessingSummary,
} from '@/services/api/types/ai-processing';

export function useAiProcessingStats() {
  return useQuery<AiProcessingStats>({
    queryKey: ['ai-processing-stats'],
    queryFn: async () => {
      const res = await api.aiProcessing.getAiProcessingStats();
      return res.data.data;
    },
    refetchInterval: data => ((data?.processing ?? 0) > 0 ? 10_000 : false),
  });
}

// Endpoints that start an AI job (e.g. /tracing-ai/bulk-summary) only ENQUEUE
// it — the AI-processing-status row is created moments later by the background
// worker. An invalidation fired right after the request can therefore still
// read an unchanged queue, and with nothing PROCESSING both queue queries stop
// polling entirely, so the navbar widget would stay stale. Re-check on these
// delays; once a refetch sees the PROCESSING row, the queries' own 10s polling
// takes over.
const QUEUE_RECHECK_DELAYS_MS = [4_000, 12_000];

export function useInvalidateAiProcessingQueue() {
  const queryClient = useQueryClient();
  return () => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
    };
    invalidate();
    for (const delay of QUEUE_RECHECK_DELAYS_MS) {
      // No cleanup needed: the query client outlives the component, and a late
      // invalidation of an inactive query is a harmless no-op.
      setTimeout(invalidate, delay);
    }
  };
}

export function useDismissAiProcessingItem() {
  return useMutation({
    mutationFn: (id: string) => api.aiProcessing.dismissAiProcessingItem(id),
  });
}

export function useCancelTracingAiSummary() {
  return useMutation({
    mutationFn: ({
      tracingId,
      traceType,
      summaryTypes,
    }: {
      tracingId: string;
      traceType: string;
      summaryTypes?: string[];
    }) =>
      api.backend.tracingAiSummaryCancel(tracingId, traceType, summaryTypes),
  });
}

// Dismiss a queue item by its asset id rather than the AI-processing-status row
// id. For AUDIT jobs the row's `assetId` IS the CAPA `workflowId`, so callers
// that only hold the workflow id (e.g. the audit-AI dialog finishing its flow)
// can clear the card without first looking up the row id.
export function useDismissAiProcessingItemByAsset() {
  return useMutation({
    mutationFn: (assetId: string) =>
      api.aiProcessing.dismissAiProcessingItemByAsset(assetId),
  });
}

export function useAiProcessingWidget() {
  const { data, isLoading, isError } = useAiProcessingStats();
  const isVisible = !isLoading && !isError;
  const isProcessing = !!data && data.processing > 0;
  return { isVisible, statsData: data, isProcessing };
}

export function useAiProcessingSummary(sortByStatus?: string) {
  return useQuery<AiProcessingSummary>({
    queryKey: ['ai-processing-summary', sortByStatus],
    queryFn: async () => {
      const res =
        await api.aiProcessing.getAiProcessingStatusSummary(sortByStatus);
      return res.data.data;
    },
    refetchInterval: data =>
      data?.items.some(item => item.status === 'PROCESSING') ? 10_000 : false,
  });
}
